import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 PRO React components
import MDBox from "../../../components/MDBox";
import MDSnackbar from "../../../components/MDSnackbar";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "../../../shared/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../../shared/Navbars/DashboardNavbar";
import Footer from "../../../shared/Footer";

// Components
import ArticleEditor from "./components/ArticleEditor";

// Services
import articleService from "../../../services/articleService";

function NewArticle() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(!!id);

  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    section: "",
    tags: "",
    institution: "",
    sources: [],
    images: [], // Replaces featuredImage, max 4
    metaTitle: "",
    metaDescription: "",
    content: [{ text: {}, imageIndex: null }], // Array of sections
  });

  const [errors, setErrors] = useState({});

  const [notification, setNotification] = useState({
    open: false,
    color: "info",
    title: "",
    content: "",
  });

  const closeNotification = () => setNotification({ ...notification, open: false });

  // The editor is now managed per-section in ArticleEditor

  // Load article if editing
  useEffect(() => {
    if (id) {
      const fetchArticle = async () => {
        setIsFetching(true);
        try {
          const res = await articleService.getById(id);
          const article = res.data;

          if (article) {
            let content = article.content || [{ text: {}, imageIndex: null }];
            // Migrate old content format to new section format if necessary
            if (content && !Array.isArray(content)) {
                content = [{ text: content, imageIndex: null }];
            }

            setFormData({
              title: article.title || "",
              subtitle: article.subtitle || "",
              section: article.section || "",
              tags: article.tags.map((tag) => tag.name).join(", ") || "",
              institution: article.institution || "",
              sources: article.sources || [],
              images: (article.gallery && article.gallery.length > 0)
                  ? article.gallery.map((img, index) => ({
                      file: img.file || img.url || img.path || img.src || img.image || (typeof img === 'string' ? img : undefined),
                      caption: img.caption || "",
                    isCover: img.section === 'Portada' || (index === 0 && !article.gallery.some(g => g.section === 'Portada'))
                  }))
                : (article.featuredImage ? [{ file: article.featuredImage, isCover: true, caption: "" }] : []),
              metaTitle: article.seo?.title || "",
              metaDescription: article.seo?.description || "",
              content: content,
            });
          }
        } catch (error) {
          console.error("Error fetching article:", error);
          setNotification({
            open: true,
            color: "error",
            title: "Error",
            content: "No se pudo cargar el artículo.",
          });
        } finally {
          setIsFetching(false);
        }
      };
      fetchArticle();
    }
  }, [id]);

  const handleSave = async () => {
    const newErrors = {};
    if (!formData.title) newErrors.title = "El título es obligatorio.";
    if (formData.title && formData.title.length > 150) newErrors.title = "El título no puede exceder los 150 caracteres.";
    if (!formData.subtitle) newErrors.subtitle = "La entradilla es obligatoria.";
    if (formData.subtitle && formData.subtitle.length > 500) newErrors.subtitle = "La entradilla no puede exceder los 500 caracteres.";
    if (!formData.section) newErrors.section = "Debes seleccionar una sección.";
    if (!formData.content || formData.content.length === 0) newErrors.content = "El artículo no puede estar vacío.";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setNotification({
        open: true,
        color: "warning",
        title: "Revisar Formulario",
        content: "Hay errores de validación. Por favor, revisa los campos en rojo.",
      });
      return;
    }

    setIsLoading(true);

    // Helper to convert blob URL to base64
    const blobToBase64 = async (blobUrl) => {
      try {
        const response = await fetch(blobUrl);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (err) {
        console.error("Error converting blob to base64:", err);
        return blobUrl;
      }
    };

    // Recursive helper to find and convert blobs in Tiptap content
    const processBlobsInContent = async (content) => {
      if (!content) return content;
      if (Array.isArray(content)) {
        return Promise.all(content.map((item) => processBlobsInContent(item)));
      }

      // Handle Tiptap nodes
      const newContent = { ...content };
      if (newContent.type === "image" && newContent.attrs?.src?.startsWith("blob:")) {
        newContent.attrs.src = await blobToBase64(newContent.attrs.src);
      }

      if (newContent.text && typeof newContent.text === "object") {
         newContent.text = await processBlobsInContent(newContent.text);
      }

      if (newContent.content && Array.isArray(newContent.content)) {
        newContent.content = await Promise.all(
          newContent.content.map((child) => processBlobsInContent(child))
        );
      }

      return newContent;
    };

    try {
      // Process all images in content sections
      const processedContent = await processBlobsInContent(formData.content);
      const dataToSave = { ...formData, content: processedContent };

      if (!id) {
        await articleService.create(dataToSave);
      } else {
        await articleService.update(id, dataToSave);
      }
      navigate("/articles");
    } catch (error) {
      console.error("Error saving article:", error);
      const errorMessage = error.response?.data?.error || "Error al guardar el artículo.";
      setNotification({
        open: true,
        color: "error",
        title: "Error",
        content: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={5} mb={9}>
        <Grid container justifyContent="center">
          {isFetching ? (
            <CircularProgress color="info" />
          ) : (
            <ArticleEditor
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              loading={isLoading}
              handleSave={handleSave}
            />
          )}
        </Grid>
      </MDBox>
      <MDSnackbar
        color={notification.color}
        icon={notification.color === "error" ? "warning" : "check"}
        title={notification.title}
        content={notification.content}
        dateTime="Ahora"
        open={notification.open}
        onClose={closeNotification}
        close={closeNotification}
        bgWhite
      />
      <Footer />
    </DashboardLayout>
  );
}

export default NewArticle;
